import { isValidEmail, isValidPhone } from "./validationHelper";
import { buildPhoneValue, getInitialPhoneParts, isMaskedPhone } from "./phoneHelper";

export const EMAIL_MAX_LENGTH = 80;

// Trims and lowercases only the domain; the local part may be case-sensitive.
export const normalizeEmail = (value) => {
  const trimmed = String(value ?? "").trim();
  const at = trimmed.lastIndexOf("@");
  if (at < 0) return trimmed;
  return `${trimmed.slice(0, at)}@${trimmed.slice(at + 1).toLowerCase()}`;
};

export const isValidContactEmail = (value) => isValidEmail(normalizeEmail(value));

export const BR_PHONE_MAX_DIGITS = 11;

export const normalizePhoneDigits = (value, maxDigits = BR_PHONE_MAX_DIGITS) =>
  String(value ?? "").replace(/\D/g, "").slice(0, maxDigits);

// Progressive mask: (00) 0000-0000 for landlines, (00) 00000-0000 for mobiles.
export const formatBrazilianPhone = (value) => {
  const digits = normalizePhoneDigits(value);
  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  const splitAt = digits.length === 11 ? 5 : 4;
  return `(${ddd}) ${rest.slice(0, splitAt)}-${rest.slice(splitAt)}`;
};

// 10 digits = DDD + landline (2-8), 11 digits = DDD + mobile (9).
export const isValidBrazilianPhone = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!/^[1-9][1-9]/.test(digits)) return false;
  if (digits.length === 11) return digits[2] === "9";
  if (digits.length === 10) return /[2-8]/.test(digits[2]);
  return false;
};

// Keeps the existing contract: E.164 such as "+5531999998888".
export const buildContactPhone = (countryCode, nationalNumber) =>
  buildPhoneValue(countryCode, String(nationalNumber ?? "").replace(/\D/g, ""));

export const isValidContactPhone = (countryCode, nationalNumber) => {
  if (countryCode === "BR" && !isValidBrazilianPhone(nationalNumber)) return false;
  return isValidPhone(buildContactPhone(countryCode, nationalNumber));
};

export const formatPhoneForDisplay = (value) => {
  if (value == null || String(value).trim() === "") return "";
  if (isMaskedPhone(value)) return String(value);
  const { countryCode, nationalNumber } = getInitialPhoneParts(String(value));
  if (countryCode === "BR" && String(value).trim().startsWith("+") && isValidBrazilianPhone(nationalNumber)) {
    return `+55 ${formatBrazilianPhone(nationalNumber)}`;
  }
  return String(value);
};

// updateClientPartyContact throws "Error <status>: <body>"; surface a JSON `message` if present.
export const extractServerMessage = (error) => {
  const match = /^Error (\d{3}): ([\s\S]*)$/.exec(error?.message || "");
  if (!match) return { status: undefined, message: "" };
  const status = Number(match[1]);
  try {
    const body = JSON.parse(match[2]);
    return { status, message: typeof body?.message === "string" ? body.message : "" };
  } catch (parseError) {
    return { status, message: "" };
  }
};
